import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Typography from '@mui/material/Typography';
import { alpha, useTheme } from '@mui/material/styles';
import { makeStyles } from '@material-ui/core/styles';
import { useNavigate } from 'react-router-dom';
import { Cell, Pie, PieChart } from 'recharts';
import { ITooltip, Text } from '../../1elements';
import { formatDate } from '../../../services/util/formatter';
import { DASHBOARD_WIDGET_TYPE } from '../../../features/dashboard-v1/dashboardContract';
import {
  buildDashboardDistributionData,
  dashboardWidgetMetricHasData,
  formatDashboardProgressPercentage,
  formatDashboardWidgetMetricValue,
  formatDashboardWidgetValue,
  getDashboardProgressColour,
  sortCalculatedDashboardWidgets,
  sortCalculatedWidgetMetrics
} from '../../../features/dashboard-v1/dashboardWidgetViewModel';

const finiteTimestamp = (value) => {
  if (!value) return null;
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

export const getLatestWidgetDate = (widgets = []) => {
  const timestamps = widgets
    .flatMap((widget) => widget.metrics ?? [])
    .map((metric) => finiteTimestamp(metric.effectiveDate))
    .filter((timestamp) => timestamp !== null);

  return timestamps.length
    ? new Date(Math.max(...timestamps)).toISOString()
    : null;
};

const getWidgetLatestDate = (widget) => getLatestWidgetDate([widget]);

const NumericVisual = ({ compact, metric, size }) => {
  const theme = useTheme();
  const hasData = dashboardWidgetMetricHasData(metric);
  const colour = metric?.colour || theme.palette.primary.main;

  return (
    <Box
      sx={{
        alignItems: 'center',
        backgroundColor: alpha(colour, hasData ? 0.14 : 0.06),
        border: '3px solid',
        borderColor: hasData ? colour : 'divider',
        borderRadius: '50%',
        color: hasData ? colour : 'text.disabled',
        display: 'flex',
        height: size,
        justifyContent: 'center',
        px: 0.5,
        width: size
      }}
    >
      <Typography
        component="span"
        sx={{
          fontSize: compact ? 10 : 11,
          fontWeight: 700,
          lineHeight: 1.05,
          maxWidth: size - 8,
          overflowWrap: 'anywhere'
        }}
      >
        {hasData ? formatDashboardWidgetMetricValue(metric) : '—'}
      </Typography>
    </Box>
  );
};

const ProgressVisual = ({ compact, widget, size }) => {
  const percentage = widget.calculation?.percentage;
  const numericPercentage = Number(percentage);
  const hasData =
    percentage !== null &&
    percentage !== undefined &&
    Number.isFinite(numericPercentage);
  const progressValue = hasData
    ? Math.min(100, Math.max(0, numericPercentage))
    : 0;
  const statusColour = getDashboardProgressColour(
    widget.calculation?.progressStatus
  );

  return (
    <Box
      aria-label={`${widget.name} progress: ${formatDashboardProgressPercentage(
        percentage
      )}`}
      data-testid="compact-progress-widget"
      role="img"
      sx={{ display: 'inline-flex', position: 'relative' }}
    >
      <CircularProgress
        aria-hidden="true"
        size={size}
        sx={{ color: 'action.hover' }}
        thickness={4.5}
        value={100}
        variant="determinate"
      />
      <CircularProgress
        aria-hidden="true"
        size={size}
        sx={{ color: statusColour, left: 0, position: 'absolute' }}
        thickness={4.5}
        value={progressValue}
        variant="determinate"
      />
      <Box
        sx={{
          alignItems: 'center',
          bottom: 0,
          display: 'flex',
          justifyContent: 'center',
          left: 0,
          position: 'absolute',
          right: 0,
          top: 0
        }}
      >
        <Typography
          color={hasData ? 'text.primary' : 'text.disabled'}
          component="span"
          sx={{
            fontSize: hasData ? (compact ? 9 : 10) : 12,
            fontWeight: 700,
            lineHeight: 1
          }}
        >
          {hasData ? formatDashboardProgressPercentage(percentage) : '—'}
        </Typography>
      </Box>
    </Box>
  );
};

const DistributionVisual = ({ widget, size }) => {
  const data = buildDashboardDistributionData(widget);
  const doughnut = widget.widgetType === DASHBOARD_WIDGET_TYPE.DOUGHNUT;
  const firstMetric = sortCalculatedWidgetMetrics(widget.metrics)[0];

  if (!data.length) {
    return (
      <Box
        data-testid="compact-empty-chart-widget"
        sx={{
          alignItems: 'center',
          border: '3px solid',
          borderColor: 'divider',
          borderRadius: '50%',
          color: 'text.disabled',
          display: 'flex',
          fontSize: 12,
          fontWeight: 700,
          height: size,
          justifyContent: 'center',
          width: size
        }}
      >
        —
      </Box>
    );
  }

  return (
    <Box
      aria-label={`${widget.name}: ${data
        .map((item) => `${item.label} ${item.formattedValue}`)
        .join(', ')}`}
      data-testid={`compact-${widget.widgetType}-widget`}
      role="img"
      sx={{ height: size, position: 'relative', width: size }}
    >
      <PieChart height={size} width={size}>
        <Pie
          animationDuration={300}
          cx="50%"
          cy="50%"
          data={data}
          dataKey="value"
          innerRadius={doughnut ? size * 0.28 : 0}
          nameKey="label"
          outerRadius={size * 0.46}
          paddingAngle={data.length > 1 ? 2 : 0}
          stroke="none"
        >
          {data.map((item) => (
            <Cell fill={item.colour} key={item.metricDefinitionId} />
          ))}
        </Pie>
      </PieChart>
      {doughnut && (
        <Typography
          component="span"
          sx={{
            fontSize: 8,
            fontWeight: 700,
            left: '50%',
            lineHeight: 1,
            maxWidth: size * 0.5,
            overflow: 'hidden',
            position: 'absolute',
            textOverflow: 'ellipsis',
            top: '50%',
            transform: 'translate(-50%, -50%)',
            whiteSpace: 'nowrap'
          }}
        >
          {formatDashboardWidgetValue(
            firstMetric,
            widget.calculation?.totalValue
          )}
        </Typography>
      )}
    </Box>
  );
};

const widgetTooltip = (widget) => {
  if (widget.description) return widget.description;
  const metrics = sortCalculatedWidgetMetrics(widget.metrics);
  if (widget.widgetType === DASHBOARD_WIDGET_TYPE.PROGRESS) {
    const metric = metrics[0];
    return `${metric?.label ?? widget.name}: ${formatDashboardWidgetMetricValue(
      metric
    )} of ${formatDashboardWidgetValue(
      metric,
      widget.calculation?.targetValue
    )}.`;
  }
  if (
    widget.widgetType === DASHBOARD_WIDGET_TYPE.PIE ||
    widget.widgetType === DASHBOARD_WIDGET_TYPE.DOUGHNUT
  ) {
    const values = metrics
      .map(
        (metric) =>
          `${metric.label}: ${formatDashboardWidgetMetricValue(metric)}`
      )
      .join('; ');
    return values || 'No approved dashboard values yet.';
  }
  return `${metrics[0]?.label ?? widget.name}: ${formatDashboardWidgetMetricValue(
    metrics[0]
  )}.`;
};

const CompactDashboardWidget = ({ compact, size, widget }) => {
  const metric = sortCalculatedWidgetMetrics(widget.metrics)[0];
  const effectiveDate = getWidgetLatestDate(widget);

  return (
    <ITooltip content={widgetTooltip(widget)} placement="bottom">
      <Box
        data-testid={`dashboard-top-widget-${widget.dashboardWidgetId}`}
        sx={{
          alignItems: 'center',
          display: 'flex',
          flex: '0 0 auto',
          flexDirection: 'column',
          minWidth: compact ? 62 : 72,
          textAlign: 'center'
        }}
      >
        {widget.widgetType === DASHBOARD_WIDGET_TYPE.NUMERIC && (
          <NumericVisual compact={compact} metric={metric} size={size} />
        )}
        {widget.widgetType === DASHBOARD_WIDGET_TYPE.PROGRESS && (
          <ProgressVisual compact={compact} size={size} widget={widget} />
        )}
        {(widget.widgetType === DASHBOARD_WIDGET_TYPE.PIE ||
          widget.widgetType === DASHBOARD_WIDGET_TYPE.DOUGHNUT) && (
          <DistributionVisual size={size} widget={widget} />
        )}
        <Typography
          component="span"
          sx={{
            fontSize: compact ? 10 : 11,
            fontWeight: 600,
            lineHeight: 1.1,
            mt: 0.5,
            overflowWrap: 'anywhere',
            width: compact ? 68 : 80
          }}
        >
          {widget.name}
        </Typography>
        <Typography
          color="text.secondary"
          component="span"
          sx={{ fontSize: compact ? 9 : 10, lineHeight: 1.1, mt: 0.25 }}
        >
          {effectiveDate
            ? formatDate(effectiveDate, 'MMM DD, YYYY')
            : 'No data'}
        </Typography>
      </Box>
    </ITooltip>
  );
};

const Widgets = ({
  className = '',
  clickable = true,
  compact = false,
  header = false,
  locationId,
  widgets = []
}) => {
  const classes = useStyles({ clickable });
  const navigate = useNavigate();
  const resolvedWidgets = sortCalculatedDashboardWidgets(widgets).slice(0, 5);
  const latestUpdateAt = getLatestWidgetDate(resolvedWidgets);
  const openDashboard = () =>
    navigate(locationId ? `/dashboard/${locationId}` : '/dashboard');

  return (
    <div
      aria-label={clickable ? 'Open dashboard' : undefined}
      className={`${className} ${classes.root}`}
      onClick={clickable ? openDashboard : undefined}
      onKeyDown={clickable ? (event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          openDashboard();
        }
      } : undefined}
      role={clickable ? 'link' : undefined}
      tabIndex={clickable ? 0 : undefined}
    >
      {header && (
        <div className={classes.latestUpdate}>
          <Text className={classes.latestUpdateText} type="widgetsHeader">
            Latest Performance
          </Text>
          <span className={classes.latestUpdateDate}>
            {latestUpdateAt ? formatDate(latestUpdateAt) : 'No approved data'}
          </span>
        </div>
      )}
      {resolvedWidgets.length > 0 ? (
        <div className={classes.widgetsDiv}>
          {resolvedWidgets.map((widget) => (
            <CompactDashboardWidget
              compact={compact}
              key={widget.dashboardWidgetId}
              size={compact ? 46 : 50}
              widget={widget}
            />
          ))}
        </div>
      ) : (
        <Typography color="text.secondary" fontSize={12}>
          No dashboard widgets configured.
        </Typography>
      )}
    </div>
  );
};

export default Widgets;

const useStyles = makeStyles({
  root: {
    cursor: ({ clickable }) => (clickable ? 'pointer' : 'default'),
    display: 'flex',
    flexDirection: 'column'
  },
  widgetsDiv: {
    display: 'flex',
    gap: '15px',
    justifyContent: 'space-between',
    overflowX: 'auto',
    paddingBottom: 2
  },
  latestUpdate: {
    alignItems: 'flex-end',
    display: 'flex',
    justifyContent: 'space-between',
    marginBottom: '15px'
  },
  latestUpdateText: {
    color: '#4B4B4B !important'
  },
  latestUpdateDate: {
    color: '#888888',
    fontSize: '12px',
    fontWeight: 400,
    marginLeft: 12
  }
});
